import { Spinner, Stack } from '@ui-components/neo.tax';
import React from 'react';

import { ProductType } from '@neo.tax/shared/graphql';
import {
  useSelectedPeriodNameOptional,
  useSelectedFiscalYearIdOptional,
  useSelectedProductOptional,
} from '@neo.tax/shared/hooks';
import { Path, ProductPeriodSelection, getPath } from '@neo.tax/shared/utils';

import { useNavigateTo } from '../navigate';
import { StyledModal } from './fiscal-period-modal.styled';
import { FiscalPeriodSelection } from './FiscalPeriodSelection';
import { useAvailableProducts } from './hooks';
import { ProductSelection } from './ProductSelection';

interface FiscalPeriodModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  companyId: string;
  selectedProduct: ProductType | null;
  defaultProduct: ProductType | null;
  defaultFiscalYearId: string | null;
  onProductPeriodChange: (value: ProductPeriodSelection) => void;
}

export const FiscalPeriodModal = ({
  companyId,
  defaultFiscalYearId,
  defaultProduct,
  isOpen,
  onProductPeriodChange,
  setIsOpen,
}: FiscalPeriodModalProps): React.ReactElement | null => {
  const navigate = useNavigateTo();
  const selectedProduct = useSelectedProductOptional();
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();
  const isClosable = !!fiscalYearId && !!periodName && !!selectedProduct;
  const { availableProducts, loading: loadingAvailableProducts } = useAvailableProducts();

  const clearSelectedProduct = (): void => {
    navigate(getPath({ path: Path.COMPANY, companyId }));
  };

  return (
    <StyledModal isOpen={isOpen} setIsOpen={isClosable ? setIsOpen : (): void => {}}>
      <Stack direction="column" overflow="hidden" style={{ display: 'block' }}>
        {loadingAvailableProducts ? (
          <Stack direction="column" alignItems="center" justifyContent="center" fullWidth paddingY={16}>
            <Spinner size="base" />
          </Stack>
        ) : (
          <>
            {selectedProduct ? (
              <FiscalPeriodSelection
                availableProducts={availableProducts}
                companyId={companyId}
                selectedProduct={selectedProduct}
                setIsOpen={setIsOpen}
                clearSelectedProduct={clearSelectedProduct}
                defaultFiscalYearId={defaultFiscalYearId}
                onProductPeriodChange={onProductPeriodChange}
              />
            ) : (
              <ProductSelection
                availableProducts={availableProducts}
                setIsOpen={setIsOpen}
                defaultProduct={defaultProduct}
              />
            )}
          </>
        )}
      </Stack>
    </StyledModal>
  );
};
